import type { Project } from '../../../types/project';
import type { Customer as GraphCustomer } from '../../../contexts/CustomerContext';
import { directSupabase } from '../../../lib/supabaseClient';
import { ensureSignedIn, GRAPH_SIGNIN_REQUIRED_TO_SEND, sendTemplateEmail } from '../../../utils/graphService';
import { customerService, type Customer as UnifiedCustomer } from '../../../services/customerService';
import { documentsService } from './documents.service';
import { createDocumentShareLink, nullifyDocumentShareLink } from './documentShareLinks.service';
import { computeTotals } from '../types/documents.types';
import { documentDisplayRef } from '../utils/invoiceLedger';
import {
  assertCanIssueCommercialDocument,
  resolveDocumentCustomerIdentity,
  splitName,
} from '../../../pages/project-workspace/workspaceBillingIdentity';

export type SendDocumentOptions = {
  versionId?: string | null;
  /**
   * When true, do not overwrite documents.status (e.g. Final reapproval transmit
   * must not downgrade an already-accepted quote back to "sent").
   */
  preserveDocumentStatus?: boolean;
  /**
   * Which survey this send belongs to. Recorded on the share link for audit labelling.
   * Defaults to 'first' for Quotation/Estimate sends.
   */
  surveyArtefact?: 'first' | 'final' | null;
  /**
   * Final reapproval may transmit a staff-locked completed Final working copy.
   * First Survey resends must unlock override first.
   */
  allowLockedTransmit?: boolean;
};

async function totalsForVersion(versionId: string) {
  const loaded = await documentsService.getVersion(versionId);
  const header: any = loaded.header || {};
  const lines = (loaded.lines || []).map((line: any) => ({
    lineKind: line.line_kind || 'other',
    qty: Number(line.qty || 0),
    unitPrice: Number(line.unit_price || 0),
    lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
    vatInclusive: !!line.vat_inclusive,
  }));
  const vatEnabled = header.vat_enabled != null ? !!header.vat_enabled : true;
  const vatPct = header.vat_pct != null ? Number(header.vat_pct) : 0.2;
  const totals = computeTotals(
    lines,
    {
      adminPct: Number(header.admin_pct || 0),
      adminCap: Number(header.admin_cap || 0),
      contingencyPct: Number(header.contingency_pct || 0),
      profitPct: Number(header.profit_pct || 0),
    },
    { enabled: vatEnabled, pct: vatPct }
  );
  const netTotal = totals.preProfit + totals.profit;
  return {
    versionNumber: header.version_number != null ? Number(header.version_number) : null,
    netTotal,
    vatAmount: vatEnabled ? netTotal * vatPct : 0,
    grossTotal: totals.grandTotal,
  };
}

function getReferralUrl(source: string): string {
  const configuredBase = String(process.env.REACT_APP_PUBLIC_BASE_URL || '').trim();
  const baseUrl = (configuredBase || window.location.origin).replace(/\/+$/g, '');
  return `${baseUrl}/refer?source=${encodeURIComponent(source)}`;
}

function toGraphCustomer(
  customer: UnifiedCustomer,
  sendingOrganizationName: string,
  identity?: { fullName: string; firstName: string | null; lastName: string | null; email: string | null; phone: string | null; address: string }
): GraphCustomer {
  const idNum = Number.isFinite(Number(customer.id)) ? Number(customer.id) : 0;
  const name = identity?.fullName || customer.name || 'Customer';
  const first = identity?.firstName || name.split(' ')[0] || '';
  const last = identity?.lastName || name.split(' ').slice(1).join(' ') || '';

  return {
    id: idNum,
    name,
    first_name: first,
    last_name: last,
    phone_number: identity?.phone || customer.phone || '',
    email: identity?.email ?? customer.email ?? null,
    address: identity?.address || customer.address || '',
    billing_address: null,
    organization_id: '',
    // NOTE: graphService uses customer.organization_name to pick the *sending* organization + template set.
    // For document sending, this should be OUR organization (Keystone Glazing/Property/Security), not the customer's company.
    organization_name: sendingOrganizationName,
    created_at: customer.created_at,
    updated_at: customer.updated_at,
    source: customer.source === 'local' ? 'local' : 'external',
  };
}

export async function sendDocumentToCustomer(
  documentId: string,
  options: SendDocumentOptions = {}
): Promise<{
  emailLogId: string;
  shareLinkId: string;
  shareUrl: string;
  sentVersionId: string;
}> {
  if (!(await ensureSignedIn())) {
    throw new Error(GRAPH_SIGNIN_REQUIRED_TO_SEND);
  }

  const doc = await documentsService.getDocument(documentId);
  if (!doc) throw new Error('Document not found');
  if (!doc.customerId && doc.accountId) {
    throw new Error('Account invoices cannot be emailed from JARVIS yet. Download the PDF, or send it from FreeAgent.');
  }
  if (!doc.projectId) throw new Error('Document has no linked project');

  const { data: projectRow, error: projErr } = await directSupabase
    .from('projects')
    .select('*')
    .eq('id', doc.projectId)
    .single();
  if (projErr) throw new Error(projErr.message || 'Failed to load project');

  // The workspace decides who the customer is; the document's own link can be stale after a customer change
  const customerId = String((projectRow as { customer_id?: string | null }).customer_id || doc.customerId || '');
  if (!customerId) throw new Error('Document has no linked customer');
  const customer = await customerService.getLocalCustomerById(customerId);
  if (!customer) throw new Error('Customer not found');
  if (String(doc.customerId || '') !== customerId) {
    const names = splitName(customer.name);
    await documentsService.updateDocument(doc.id, {
      customerId,
      customerFirstName: names.firstName,
      customerLastName: names.lastName,
      customerTitle: customer.title || null,
    });
  }

  // Resolve sending organization from the document (not from customer.company)
  const { data: orgRow, error: orgErr } = await directSupabase
    .from('organisations')
    .select('name')
    .eq('id', doc.organisationId)
    .single();
  if (orgErr) throw new Error(orgErr.message || 'Failed to load organisation');
  const sendingOrganizationName = String((orgRow as any)?.name || '').trim();
  if (!sendingOrganizationName) throw new Error('Missing sending organisation name');

  assertCanIssueCommercialDocument({
    project: projectRow as { use_billing_details_for_documents?: boolean | null },
    customer,
    kind: doc.kind,
  });
  const identity = resolveDocumentCustomerIdentity({
    project: projectRow as { use_billing_details_for_documents?: boolean | null },
    customer,
    kind: doc.kind,
  });
  if (!identity.email) throw new Error('Customer email not found');

  let sentVersionId: string | null = null;
  let sentVersionNumber: number | null = null;
  let sourceVersionId: string | null = null;
  let sentNetTotal = doc.netTotal ?? null;
  let sentVatAmount = doc.vatAmount ?? null;
  let sentGrossTotal = doc.grossTotal ?? null;
  if (doc.kind !== 'Invoice') {
    let workingVersionId = String(options.versionId || '').trim() || null;
    if (!workingVersionId) {
      workingVersionId = String(doc.activeVersionId || '').trim() || null;
    }
    if (!workingVersionId) {
      const activeVersion = await documentsService.getActiveVersion(documentId);
      workingVersionId = activeVersion?.header?.id ? String(activeVersion.header.id) : null;
    }
    if (!workingVersionId) {
      throw new Error('No quote working record found to send');
    }

    const artefact = options.surveyArtefact === 'final'
      ? 'final'
      : options.surveyArtefact === 'first'
        ? 'first'
        : 'first';

    // First Survey may be transmitted only once — then permanently immutable.
    if (artefact === 'first') {
      const { data: lockRow, error: lockErr } = await directSupabase
        .from('document_versions')
        .select('is_transmitted, override_unlocked')
        .eq('id', workingVersionId)
        .maybeSingle();
      if (lockErr) throw new Error(lockErr.message);
      if (lockRow?.is_transmitted) {
        throw new Error(
          'First Survey has already been transmitted and is permanently immutable. Update Final Survey instead.',
        );
      }
      if (doc.projectId) {
        const { visualDesignerSurveysService } = await import('./visualDesignerSurveys.service');
        if (await visualDesignerSurveysService.projectFirstSurveyIsImmutable(String(doc.projectId))) {
          throw new Error(
            'First Survey has already been transmitted and is permanently immutable. Update Final Survey instead.',
          );
        }
        await visualDesignerSurveysService.alignFirstSheetForTransmit(String(doc.projectId), workingVersionId);
      }
    }

    if (!options.allowLockedTransmit && artefact !== 'first') {
      const { data: lockRow, error: lockErr } = await directSupabase
        .from('document_versions')
        .select('is_transmitted, override_unlocked')
        .eq('id', workingVersionId)
        .maybeSingle();
      if (lockErr) throw new Error(lockErr.message);
      if (lockRow?.is_transmitted && !lockRow?.override_unlocked) {
        throw new Error(
          'This survey is locked after transmission. Reopen Final Survey before sending again.',
        );
      }
    }

    // Always mint an immutable transmission snapshot for the customer link.
    // Then lock the working record so further edits require Unlock (fork).
    const transmission = await documentsService.createImmutableTransmission(workingVersionId);
    sentVersionId = transmission.id;
    sourceVersionId = workingVersionId;
    await documentsService.markVersionTransmitted(workingVersionId);

    const snapshot = await totalsForVersion(sentVersionId);
    sentVersionNumber = snapshot.versionNumber ?? transmission.versionNumber;
    sentNetTotal = snapshot.netTotal;
    sentVatAmount = snapshot.vatAmount;
    sentGrossTotal = snapshot.grossTotal;
  }

  const surveyArtefact = options.surveyArtefact === 'final'
    ? 'final' as const
    : options.surveyArtefact === 'first'
      ? 'first' as const
      : (doc.kind === 'Invoice' ? null : 'first' as const);

  const share = await createDocumentShareLink(documentId, {
    sentVersionId,
    sentVersionNumber,
    sourceVersionId,
    surveyArtefact,
    sentNetTotal,
    sentVatAmount,
    sentGrossTotal,
  });
  const isNewlyMintedShareLink = !!String(share.token || '').trim();

  const cleanupUnsentShareLink = async () => {
    // Only clean up links minted for this send attempt.
    // Reused links belong to previously-sent transmissions and must be retained.
    if (!isNewlyMintedShareLink) return;
    try {
      await nullifyDocumentShareLink(share.id, 'Send aborted before email was delivered');
    } catch {
      // Best effort: never hide the original send error due to cleanup issues.
    }
  };

  const displayRef = documentDisplayRef(doc);
  const variables: Record<string, string> = {
    secureUrl: share.url,
    shareUrl: share.url,
    referralUrl: '',
    documentId,
    documentKind: doc.kind,
    lifecycleRef: doc.lifecycleRef,
    invoiceRef: displayRef,
    shareLinkId: share.id,
    sentVersionId: sentVersionId || '',
    sentVersionNumber: sentVersionNumber != null ? String(sentVersionNumber) : '',
  };

  const templateType =
    doc.kind === 'Invoice'
      ? 'sendCustomerInvoice'
      : 'sendEstimateQuote';
  variables.referralUrl = getReferralUrl(templateType);

  let res: any;
  try {
    res = await sendTemplateEmail({
      templateType,
      project: projectRow as unknown as Project,
      customer: toGraphCustomer(customer, sendingOrganizationName, identity),
      variables,
      updateWorkflowTask: false,
      captureGraphIds: true,
    } as any);
  } catch (e) {
    await cleanupUnsentShareLink();
    throw e;
  }

  if (res.status !== 'sent') {
    await cleanupUnsentShareLink();
    // Defensive: sendTemplateEmail should return 'sent' here (it throws on error),
    // but don't update document status if it didn't send.
    throw new Error('Email was not sent');
  }

  // First transmit: live customer document starts as this immutable First snapshot.
  // (Final "Update without reapproval" / Final accept later advance this pointer.)
  if (surveyArtefact === 'first' && sentVersionId && doc.kind !== 'Invoice') {
    try {
      await directSupabase
        .from('documents')
        .update({ customer_live_version_id: sentVersionId })
        .eq('id', documentId);
    } catch (err) {
      console.warn('Failed to set customer live version after First transmit:', err);
    }
  }

  // Never downgrade a decided quote back to "sent" when transmitting again
  // (e.g. Final Survey reapproval). Invoices still only promote draft → sent.
  const currentStatus = String(doc.status || '').trim().toLowerCase();
  const terminalQuoteStatuses = new Set(['accepted', 'invoiced', 'paid', 'cash', 'rejected', 'void']);
  const preserveStatus = !!options.preserveDocumentStatus || terminalQuoteStatuses.has(currentStatus);

  if (!preserveStatus) {
    if (doc.kind === 'Invoice') {
      if (currentStatus === 'draft') {
        await documentsService.updateDocument(documentId, { status: 'sent' });
      }
    } else {
      await documentsService.updateDocument(documentId, { status: 'sent' });
    }
  }

  // Create a persistent in-app notification (cross-device) so staff see it until acknowledged.
  try {
    const customerName = String(identity.fullName || customer.name || '').trim() || 'Customer';
    await directSupabase.from('notifications').insert({
      type: 'doc_sent',
      message: `${doc.kind} sent: ${displayRef} → ${customerName}`,
      metadata: {
        documentId,
        shareLinkId: share.id,
        emailLogId: res.emailLogId,
        kind: doc.kind,
        lifecycleRef: doc.lifecycleRef,
        invoiceRef: doc.invoiceRef || null,
        customerId,
        projectId: doc.projectId,
        organisationId: doc.organisationId,
        sentVersionId: sentVersionId || '',
      },
    });
  } catch {
    // Notifications should never block sending.
  }

  if (doc.projectId) {
    const { emitActivityEventBackground } = await import('../../../services/activityEvents.service');
    const noun = doc.kind === 'Invoice' ? 'Invoice' : doc.kind === 'Estimate' ? 'Estimate' : 'Quote';
    emitActivityEventBackground({
      action: 'document.sent',
      entityType: 'document',
      entityId: documentId,
      projectId: doc.projectId,
      label: `${noun} emailed to customer${displayRef ? ` (${displayRef})` : ''}`,
      metadata: {
        kind: doc.kind,
        lifecycleRef: doc.lifecycleRef,
        invoiceRef: doc.invoiceRef || null,
        shareLinkId: share.id,
        emailLogId: res.emailLogId,
      },
    });
  }

  // Quote First transmit: freeze First and provision Final on the quote.
  // Estimate First has no Final stage — accept later rolls to a quote, then Final is created.
  if (
    surveyArtefact === 'first'
    && doc.projectId
    && doc.kind === 'Quotation'
    && sentVersionId
  ) {
    try {
      const { visualDesignerSurveysService } = await import('./visualDesignerSurveys.service');
      await visualDesignerSurveysService.createFinalSurveyFromFirst(String(doc.projectId), {
        fromTransmissionVersionId: sentVersionId,
      });
    } catch (err) {
      console.error('Final Survey provision after First transmit failed:', err);
      // Transmit already succeeded — surface via activity but do not roll back the customer send.
      try {
        const { emitActivityEventBackground } = await import('../../../services/activityEvents.service');
        emitActivityEventBackground({
          action: 'survey.final_created',
          entityType: 'project',
          entityId: String(doc.projectId),
          projectId: String(doc.projectId),
          label: 'Final Survey provision failed after First transmit — retry from workspace',
          metadata: {
            error: err instanceof Error ? err.message : String(err),
            sentVersionId,
            documentId,
          },
        });
      } catch {
        // ignore
      }
    }
  }

  return {
    emailLogId: res.emailLogId,
    shareLinkId: share.id,
    shareUrl: share.url,
    sentVersionId: sentVersionId || '',
  };
}

